import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDotBotsAuth } from "@dotbots-boutique/auth-sdk/react";
import { useApi } from "../lib/api";
import { formatDate } from "../lib/format";
import { hasPermission } from "../lib/permissions";
import { useT } from "../i18n/I18nProvider";
import type { Contact } from "@shared/types";

interface ContactDetail {
  contact: Contact;
  tickets: { id: string; ticket_number: number; subject: string; status: string; priority: string; created_at: string }[];
}

type CustomerType = "B2B" | "B2C" | "VIP";

interface ContactForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  customerType: CustomerType;
  consentEmail: boolean;
  consentMarketing: boolean;
}

const EMPTY_NEW_CONTACT: ContactForm = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  customerType: "B2C",
  consentEmail: true,
  consentMarketing: false,
};

function contactToForm(c: Contact): ContactForm {
  return {
    firstName: c.first_name ?? "",
    lastName: c.last_name ?? "",
    email: c.email ?? "",
    phone: c.phone ?? "",
    customerType: (c.customer_type as CustomerType) ?? "B2C",
    consentEmail: c.consent_email ?? false,
    consentMarketing: c.consent_marketing ?? false,
  };
}

export default function ContactList() {
  const api = useApi();
  const t = useT();
  const { user } = useDotBotsAuth();
  const canCreate = hasPermission(user?.roles ?? [], "contacts.write");
  const canEdit = hasPermission(user?.roles ?? [], "contacts.write");

  const [searchParams, setSearchParams] = useSearchParams();
  const openId = searchParams.get("open");
  const wantsEdit = searchParams.get("edit") === "1";

  const [search, setSearch] = useState("");
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selected, setSelected] = useState<ContactDetail | null>(null);

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<ContactForm>(EMPTY_NEW_CONTACT);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [okMessage, setOkMessage] = useState<string | null>(null);

  const load = async () => {
    const params = search ? `?q=${encodeURIComponent(search)}` : "";
    const res = await api.get<{ data: Contact[] }>(`/api/contacts${params}`);
    setContacts(res.data);
  };

  useEffect(() => {
    const id = setTimeout(load, 200);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Opening a contact now drops the operator straight into the
  // editable form view + the per-contact ticket list (single screen).
  // Read-only viewers still get the same form, just with disabled
  // inputs because canEdit gates the editing controls below.
  const openContact = useCallback(async (id: string) => {
    const res = await api.get<ContactDetail>(`/api/contacts/${id}`);
    setSelected(res);
    setDraft(contactToForm(res.contact));
    setEditing(canEdit);
    setCreating(false);
    return res;
  }, [api, canEdit]);

  // Step back from the detail/edit view to the list. Clears the
  // ?open / ?edit params so a refresh doesn't pop the form straight
  // back open.
  const backToList = () => {
    setSelected(null);
    setEditing(false);
    setError(null);
    if (openId || wantsEdit) {
      const next = new URLSearchParams(searchParams);
      next.delete("open");
      next.delete("edit");
      setSearchParams(next, { replace: true });
    }
  };

  // Honour ?open=<id>&edit=1 deep links from elsewhere in the app (e.g.
  // the ticket detail customer card). Auto-loads the contact and, if
  // edit=1, drops straight into the edit form.
  useEffect(() => {
    if (!openId) return;
    if (selected?.contact.id === openId) {
      if (wantsEdit && canEdit && !editing) {
        setDraft(contactToForm(selected.contact));
        setEditing(true);
      }
      return;
    }
    openContact(openId).then((res) => {
      if (wantsEdit && canEdit) {
        setDraft(contactToForm(res.contact));
        setEditing(true);
      }
    }).catch((err) => setError((err as Error).message));
  }, [openId, wantsEdit, canEdit, selected, editing, openContact]);

  const startCreate = () => {
    setError(null);
    setOkMessage(null);
    setDraft(EMPTY_NEW_CONTACT);
    setCreating(true);
    setEditing(false);
  };

  const cancelCreate = () => {
    setCreating(false);
    setError(null);
  };

  // Cancel = back to the contacts list. The previous incarnation
  // toggled out of edit-only mode; with the new flow there is no
  // "view-only" sub-state to land on, so cancelling drops the
  // selection entirely.
  const cancelEdit = backToList;

  const submitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const res = await api.post<{ id: string }>("/api/contacts", {
        firstName: draft.firstName.trim(),
        lastName: draft.lastName.trim(),
        email: draft.email.trim() || null,
        phone: draft.phone.trim() || null,
        customerType: draft.customerType,
        consentEmail: draft.consentEmail,
        consentMarketing: draft.consentMarketing,
      });
      setCreating(false);
      setOkMessage(t("contacts.created"));
      await load();
      await openContact(res.id);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const submitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    setSaving(true);
    setError(null);
    try {
      await api.patch(`/api/contacts/${selected.contact.id}`, {
        firstName: draft.firstName.trim(),
        lastName: draft.lastName.trim(),
        email: draft.email.trim() || null,
        phone: draft.phone.trim() || null,
        customerType: draft.customerType,
        consentEmail: draft.consentEmail,
        consentMarketing: draft.consentMarketing,
      });
      setOkMessage(t("contacts.updated"));
      // Re-fetch the row so the form reflects whatever the backend
      // normalised + refresh the underlying list. `openContact` puts
      // us back in the edit view (which is where we already are).
      await load();
      await openContact(selected.contact.id);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  // Shared form body for both create and edit so we don't duplicate the
  // field markup. The submit handler and CTA label differ per mode.
  const renderForm = (mode: "create" | "edit") => (
    <form className="card" onSubmit={mode === "create" ? submitCreate : submitEdit}>
      <h3 style={{ marginTop: 0 }}>
        {mode === "create" ? t("contacts.newHeading") : t("contacts.editHeading")}
      </h3>

      <div className="form-grid form-grid-2">
        <div className="field">
          <label>{t("common.firstName")} <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>({t("common.required")})</span></label>
          <input required value={draft.firstName} onChange={(e) => setDraft({ ...draft, firstName: e.target.value })} />
        </div>
        <div className="field">
          <label>{t("common.lastName")} <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>({t("common.required")})</span></label>
          <input required value={draft.lastName} onChange={(e) => setDraft({ ...draft, lastName: e.target.value })} />
        </div>
      </div>

      <div className="field">
        <label>{t("common.email")}</label>
        <input type="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
      </div>

      <div className="field">
        <label>{t("common.phone")}</label>
        <input value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} />
      </div>

      <div className="field" style={{ maxWidth: 260 }}>
        <label>{t("contacts.customerType")}</label>
        <select value={draft.customerType} onChange={(e) => setDraft({ ...draft, customerType: e.target.value as CustomerType })}>
          <option value="B2C">{t("contacts.customerTypeB2C")}</option>
          <option value="B2B">{t("contacts.customerTypeB2B")}</option>
          <option value="VIP">{t("contacts.customerTypeVIP")}</option>
        </select>
      </div>

      <div className="field">
        <label className="inline-checkbox">
          <input
            type="checkbox"
            checked={draft.consentEmail}
            onChange={(e) => setDraft({ ...draft, consentEmail: e.target.checked })}
          />
          {t("contacts.consentEmail")}
        </label>
      </div>
      <div className="field">
        <label className="inline-checkbox">
          <input
            type="checkbox"
            checked={draft.consentMarketing}
            onChange={(e) => setDraft({ ...draft, consentMarketing: e.target.checked })}
          />
          {t("contacts.consentMarketing")}
        </label>
      </div>

      {error && <div style={{ color: "var(--danger)", marginBottom: 12 }}>{error}</div>}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button type="button" onClick={mode === "create" ? cancelCreate : cancelEdit}>{t("common.cancel")}</button>
        <button
          type="submit"
          className="primary"
          disabled={saving || !draft.firstName.trim() || !draft.lastName.trim()}
        >
          {saving
            ? (mode === "create" ? t("common.creating") : t("common.saving"))
            : (mode === "create" ? t("common.create") : t("common.save"))}
        </button>
      </div>
    </form>
  );

  // Selecting a contact replaces the list with a single screen: the
  // editable form for that contact + its tickets table. Read-only
  // viewers (canEdit === false) still get the form but the inputs
  // inside renderForm fall back to disabled via the same draft state.
  const detailMode = selected !== null;

  return (
    <>
      <div className="toolbar">
        <h2>{t("contacts.title")}</h2>
        {detailMode ? (
          <button onClick={backToList}>← {t("contacts.backToList")}</button>
        ) : (
          canCreate && !creating && (
            <button className="primary" onClick={startCreate}>{t("contacts.newButton")}</button>
          )
        )}
      </div>

      {okMessage && <div className="card" style={{ color: "var(--ok)" }}>{okMessage}</div>}

      {creating && renderForm("create")}

      {detailMode ? (
        <>
          {/* Header card: name + a quick link to the filtered ticket
              list. The edit form below repeats the name/email fields
              for editing — this card is just the at-a-glance summary. */}
          <div className="card" style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
            <div style={{ minWidth: 0 }}>
              <h3 style={{ marginTop: 0, marginBottom: 4 }}>
                {selected.contact.first_name} {selected.contact.last_name}
              </h3>
              <div style={{ color: "var(--text-muted)", fontSize: 13 }}>
                {selected.contact.email} {selected.contact.phone && `· ${selected.contact.phone}`}
              </div>
            </div>
            <div style={{ color: "var(--text-muted)", fontSize: 13 }}>
              {t("contacts.ticketsHeading")}:{" "}
              <strong>{selected.contact.ticket_count ?? selected.tickets.length}</strong>
            </div>
          </div>

          {/* Edit form for the selected contact. If the operator
              lacks contacts.write the form still renders but the
              submit button is disabled by the existing canEdit
              guard inside renderForm. */}
          {canEdit && renderForm("edit")}

          {/* Tickets the selected contact has — full table on this
              screen so the operator doesn't have to jump elsewhere
              just to see the list. The "View all" link still goes
              to the filtered ticket list for sort/filter/pagination. */}
          <div className="card">
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
              <h4 style={{ margin: 0 }}>{t("contacts.ticketsHeading")}</h4>
              {(selected.contact.ticket_count ?? selected.tickets.length) > 0 && (
                <a
                  href={`#/tickets?contactId=${encodeURIComponent(selected.contact.id)}`}
                  style={{ fontSize: 13 }}
                >
                  {t("contacts.viewAllTickets")} →
                </a>
              )}
            </div>
            <table>
              <thead>
                <tr>
                  <th>{t("ticket.number")}</th>
                  <th>{t("ticket.subject")}</th>
                  <th>{t("ticket.status")}</th>
                  <th>{t("ticket.priority")}</th>
                  <th>{t("ticket.created")}</th>
                </tr>
              </thead>
              <tbody>
                {selected.tickets.map((tk) => (
                  <tr key={tk.id}>
                    <td><a href={`#/tickets/${tk.id}`}>#{tk.ticket_number}</a></td>
                    <td>{tk.subject}</td>
                    <td>{tk.status}</td>
                    <td>{tk.priority}</td>
                    <td>{formatDate(tk.created_at)}</td>
                  </tr>
                ))}
                {selected.tickets.length === 0 && (
                  <tr><td colSpan={5} style={{ color: "var(--text-muted)" }}>{t("contacts.noTickets")}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="card">
          <div className="field">
            <label>{t("common.search")}</label>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("contacts.searchPlaceholder")} />
          </div>
          <table>
            <thead>
              <tr>
                <th>{t("contacts.tableName")}</th>
                <th>{t("contacts.tableEmail")}</th>
                <th>{t("contacts.tablePhone")}</th>
                <th>{t("contacts.tableType")}</th>
                <th>{t("contacts.tableCreated")}</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((c) => (
                <tr key={c.id} onClick={() => openContact(c.id)} style={{ cursor: "pointer" }}>
                  <td>{c.first_name} {c.last_name}</td>
                  <td>{c.email ?? "—"}</td>
                  <td>{c.phone ?? "—"}</td>
                  <td>{c.customer_type}</td>
                  <td>{formatDate(c.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
